// Unique content for each commercial service / location page.
// Every entry has distinct, useful copy — no duplicated boilerplate.

export const SERVICE_PAGES = {
  "property-snagging-dubai": {
    breadcrumb: "Property Snagging Dubai",
    title: "Property Snagging Dubai | Independent Inspection | Fedari",
    description: "Book an independent property snagging inspection in Dubai. Document visible finishing and installation concerns before handover. Request a fixed quote from Fedari.",
    h1: "Property Snagging Inspections in Dubai",
    serviceType: "Property snagging inspection",
    intro: "Before accepting a newly completed property, have its visible condition documented independently. Fedari inspects the agreed accessible areas of apartments, villas and townhouses in Dubai and records relevant finishing, workmanship and installation concerns for follow-up.",
    whatWeInspect: [
      "Walls, ceilings, paint finish, plaster and visible cracks",
      "Floor and wall tiling, grout, skirting and level",
      "Doors, frames, handles, locks, alignment and swing",
      "Windows, glazing, seals and balcony doors",
      "Kitchen and bathroom joinery, cabinets and worktops",
      "Sealant and silicone lines around wet areas",
      "Functional water flow and pressure observations where safe access and operating conditions allow",
      "Visible electrical fittings, sockets and switches",
      "Air-conditioning: operational, temperature and airflow observations within the agreed inspection scope",
      "Accessible external areas, terraces and common finishes",
    ],
    whoFor: [
      "Buyers taking handover of a newly built apartment or villa",
      "Owners approaching the end of their defect liability period",
      "Investors who want documented evidence before signing off",
      "Anyone who wants an independent second opinion on build quality",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Penthouses"],
    process: [
      "You book a slot online or request a quote — we confirm the details.",
      "Our inspector attends the property with calibrated tools and cameras.",
      "Relevant visible findings within the agreed scope are documented by room with photographic evidence where practicable, prioritised where supported and given a recommended next action.",
      "You receive a structured PDF report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
      "You submit the report to the responsible party for follow-up under the applicable process.",
    ],
    equipment: [
      "Moisture meters for damp and leak indications",
      "Thermal imaging, where included in the agreed scope, may reveal observable temperature anomalies; it does not see through finishes or confirm concealed defects",
      "Spirit and laser levels for tiling and joinery",
      "High-resolution cameras for photographic evidence",
    ],
    deliverables: [
      "A structured snagging report grouped by room and system",
      "Every snag photographed and rated Critical / Major / Minor / Advisory",
      "A clear recommended next action for each item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Our snagging inspections are visual and non-destructive. We report on what is visible and accessible on the day and do not open, dismantle or damage any part of the property. Concealed defects behind finishes cannot always be identified through a visual inspection.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Tell us about any uncommissioned systems or access restrictions before booking so we can confirm what can be checked.",
    faqs: [
      { q: "Is snagging the same as a structural survey?", a: "No. This is a visual, non-destructive inspection of agreed accessible areas. It is not a structural engineering assessment, valuation or statutory certification." },
      { q: "Does the developer have to repair every finding?", a: "The report provides observations for follow-up. Responsibility, acceptance of findings and any rectification depend on the relevant contract and process; the inspection itself does not guarantee repairs." },
      { q: "Is a reinspection included?", a: "A return visit to review reported rectification is a separate service unless your accepted quotation explicitly includes it. Ask us to quote the follow-up scope before booking." },
      { q: "What is property snagging in Dubai?", a: "Snagging is an independent inspection that identifies findings in workmanship, finishing and building services in a new or newly finished property, so they can be documented before or shortly after handover for timely follow-up." },
      { q: "How much does a snagging inspection cost in Dubai?", a: "Snagging inspection fees depend on property type, built-up area, location and the agreed scope. Request a quote and we will confirm the fixed fee before you book." },
      { q: "How soon do I get the report?", a: "You receive a structured photo report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
      { q: "Should I snag before or after handover?", a: "Ideally a snagging inspection is carried out before you sign the handover, or as early as possible within your applicable period, so visible findings are documented for timely follow-up." },
    ],
    related: [
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
      { label: "Villa inspection Dubai", href: "/villa-inspection-dubai" },
      { label: "DLP / warranty inspection", href: "/dlp-warranty-inspection-dubai" },
    ],
  },

  "handover-inspection-dubai": {
    breadcrumb: "Handover Inspection Dubai",
    title: "Pre-Handover Inspection Dubai | Independent Checks | Fedari",
    description: "Arrange a pre-handover inspection in Dubai. Independent visual checks and a photographic report for follow-up before or around acceptance. Request a Fedari quote.",
    h1: "Pre-Handover Property Inspections in Dubai",
    serviceType: "Pre-handover inspection",
    intro: "Have the property inspected independently before or around handover, subject to permitted access. Fedari documents relevant visible finishing, installation and completeness concerns within the agreed scope, helping you raise clear observations during the handover process.",
    whatWeInspect: [
      "Visible workmanship, completeness and finishing observations within the agreed scope",
      "Paint, plaster, tiling and joinery finish",
      "Doors, windows, ironmongery and alignment",
      "Kitchen and bathroom fit-out and sealant",
      "Visible plumbing, electrical and drainage",
      "Air-conditioning operation and airflow balance",
      "Visible incomplete or defective workmanship observed at the time of inspection",
    ],
    whoFor: [
      "Buyers about to accept keys on an off-plan or new-build property",
      "Owners who want defects fixed before they sign the handover form",
      "Anyone who wants an independent record before taking responsibility",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Penthouses"],
    process: [
      "Book your inspection to align with your handover appointment.",
      "We inspect the agreed safe, accessible and observable areas before or around formal handover, subject to lawful access and the applicable developer process.",
      "Each defect is photographed, rated and given a recommended next action.",
      "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
      "Submit the documented findings to the responsible party and track the response under the applicable handover, contract or warranty process.",
    ],
    equipment: [
      "Moisture meters and, where included, thermal imaging",
      "Levels and measuring tools for finishing tolerances",
      "High-resolution photographic documentation",
    ],
    deliverables: [
      "A structured pre-handover report prepared for submission or follow-up through the applicable developer, contract or warranty process",
      "Photographic evidence of relevant visible findings where practicable",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Pre-handover inspections are visual and non-destructive. We assess accessible areas on the day of inspection and cannot identify concealed defects behind completed finishes.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Tell us about any uncommissioned systems or access restrictions before booking so we can confirm what can be checked.",
    faqs: [
      { q: "When should I request an inspection?", a: "As soon as you have an expected handover or permitted access date. We confirm availability and scope around the actual access arrangements." },
      { q: "Can you inspect after keys have been accepted?", a: "An inspection may still document the observable condition, subject to access and the agreed scope. Any rights or deadlines depend on the applicable contract and process." },
      { q: "Can Fedari sign the handover documents for me?", a: "The inspection service does not by itself appoint Fedari to accept the property or sign legal documents on your behalf. Any representation must be separately and expressly agreed." },
      { q: "When should the handover inspection happen?", a: "Ideally, arrange the inspection before or around formal handover, subject to lawful access and the applicable developer process. This allows visible findings to be documented for timely follow-up." },
      { q: "Do I need to attend the inspection?", a: "You are welcome to attend but it is not required. We only need safe, lawful access to the property." },
      { q: "What happens after I receive the report?", a: "You submit the documented findings to the responsible party and track the response under the applicable handover, contract or warranty process. The report gives you clear, structured photographic evidence for that follow-up." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Apartment inspection Dubai", href: "/apartment-inspection-dubai" },
      { label: "DLP / warranty inspection", href: "/dlp-warranty-inspection-dubai" },
    ],
  },

  "villa-inspection-dubai": {
    breadcrumb: "Villa Inspection Dubai",
    title: "Villa Snagging & Inspection Dubai | Fedari",
    description: "Independent villa snagging in Dubai, with internal and agreed accessible external areas. Clear photographic findings and a confirmed scope. Request a Fedari quote.",
    h1: "Villa Snagging and Inspection in Dubai",
    serviceType: "Villa inspection",
    image: {
      base: "/images/fedari-plaque-villa",
      widths: [800, 1536],
      w: 1536,
      h: 1024,
      alt: "Fedari 'Inspected by Fedari' plaque at a villa entrance in Dubai",
    },
    priceMode: "villa",
    priceNote: "A standard villa inspection starts from AED 2,499. Large or non-standard villas (extra floors, basements, large plots or specialist areas) are priced as a fixed quote after a short scope review.",
    intro: "A villa inspection needs a scope that reflects its floors, rooms and external areas. Fedari independently records relevant visible findings in the agreed safe and accessible areas of villas and townhouses in Dubai, for handover or property-condition follow-up.",
    whatWeInspect: [
      "Agreed accessible floors, staircases and internal rooms",
      "Safe and accessible roof areas, parapets and visible external walls, where included in the confirmed scope",
      "Garden, driveway, boundary walls and gates where included in the agreed scope and safely accessible",
      "Windows, doors, shutters and external glazing",
      "Kitchen, bathrooms, joinery and built-in wardrobes",
      "Functional water flow and pressure observations where safe access and operating conditions allow across floors",
      "Electrical distribution board, sockets and fittings",
      "Air-conditioning units, airflow and condensate",
      "Visible observations of accessible pool plant and equipment where included — not a specialist pool, water-quality or system-performance certification",
    ],
    whoFor: [
      "Buyers taking handover of a new villa or townhouse",
      "Owners nearing the end of the villa's defect liability period",
      "Families who want the safe, accessible areas of the property checked before moving in",
    ],
    propertyTypes: ["Villas", "Townhouses", "Independent houses"],
    process: [
      "Book a villa inspection — larger plots may need a longer slot.",
      "We inspect the agreed accessible floors and, where safe and included, accessible roof and external areas.",
      "Each relevant visible finding is recorded with its location, photographic evidence where practicable, priority classification where supported and a recommended next action.",
      "You receive the full villa report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
    ],
    equipment: [
      "Moisture meters for roofs, wet areas and external walls",
      "Thermal imaging, where included in the agreed scope, may reveal observable temperature anomalies; it does not see through finishes or confirm concealed defects",
      "Levels for tiling, joinery and floors across levels",
      "High-resolution cameras for documentation",
    ],
    deliverables: [
      "A multi-level villa report grouped by floor and system",
      "Photographic evidence of relevant visible findings where practicable",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Villa inspections are visual and non-destructive. Areas that are unsafe or inaccessible on the day — such as certain roof sections or locked plant rooms — are noted as not inspected.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Tell us about any uncommissioned systems or access restrictions before booking so we can confirm what can be checked.",
    faqs: [
      { q: "Are the roof and pool always included?", a: "No. These areas must be included in the confirmed scope and safe to access on the day. Visible pool observations are not a specialist pool, water-quality or performance certification." },
      { q: "Can you inspect a townhouse?", a: "Yes. Share the floor layout and any accessible external areas so the scope and appointment can be matched to the property." },
      { q: "Does thermal imaging show every hidden defect?", a: "No. Where agreed and appropriate, thermal imaging may indicate observable temperature differences. It does not see through finishes or guarantee the detection of concealed defects." },
      { q: "How long does a villa inspection take?", a: "It depends on the size and number of floors, but villas generally take longer than apartments because of the additional external and roof areas." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
      { label: "Resale property inspection", href: "/resale-property-inspection-dubai" },
    ],
  },

  "apartment-inspection-dubai": {
    breadcrumb: "Apartment Inspection Dubai",
    title: "Apartment Snagging & Inspection Dubai | Fedari",
    description: "Independent apartment snagging in Dubai for studios, apartments and penthouses. Photographic findings, clear scope and a fixed fee before booking with Fedari.",
    h1: "Apartment Snagging and Inspection in Dubai",
    serviceType: "Apartment inspection",
    intro: "Get an independent view of the apartment before handover, purchase or occupancy. Fedari documents relevant visible findings across the agreed rooms, fit-out and accessible services, giving you a room-by-room record for follow-up with the responsible party.",
    whatWeInspect: [
      "All rooms, hallways and built-in storage",
      "Paint, plaster, tiling and skirting finish",
      "Doors, windows, balcony doors and glazing seals",
      "Kitchen units, worktops and appliances fit-out",
      "Bathroom fittings, sealant and drainage",
      "Visible plumbing and water pressure",
      "Electrical sockets, switches and fittings",
      "Air-conditioning: operational, temperature and airflow observations within the agreed inspection scope",
      "Balcony finish and drainage",
    ],
    whoFor: [
      "Buyers taking handover of a new apartment",
      "Tenants or owners who want an independent condition check",
      "Investors who want documented evidence before sign-off",
    ],
    propertyTypes: ["Studios", "Apartments", "Penthouses"],
    process: [
      "Book an apartment inspection at a time that suits you.",
      "We inspect the agreed safe and accessible areas and make observations of accessible building services within the confirmed scope.",
      "Each relevant visible finding is recorded with its location, photographic evidence where practicable, priority classification where supported and a recommended next action.",
      "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
    ],
    equipment: [
      "Moisture meters for wet areas and balconies",
      "Thermal imaging where included",
      "Levels for tiling and joinery",
      "High-resolution cameras",
    ],
    deliverables: [
      "A structured apartment report by room and system",
      "Photographic evidence of relevant visible findings where practicable",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Apartment inspections are visual and non-destructive. Common areas outside the demised unit and concealed services are outside the scope unless separately agreed.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Tell us about any uncommissioned systems or access restrictions before booking so we can confirm what can be checked.",
    faqs: [
      { q: "Can you inspect a furnished apartment?", a: "Yes, subject to the confirmed scope and safe access. Furniture and belongings can conceal areas; we document relevant access limitations rather than assume those areas are defect-free." },
      { q: "Do you inspect studios and penthouses?", a: "Yes. Share the layout, approximate size and any terraces or other special areas so we can confirm the inspection scope and fee." },
      { q: "Does an apartment inspection cover the whole building?", a: "No. The standard scope concerns the agreed apartment areas. Common areas, building-wide systems and specialist investigations require separate scope confirmation." },
      { q: "Is snagging worth it for a small apartment?", a: "Yes — even studios and one-bedroom apartments regularly have finishing, sealant and AC snags that are worth documenting early for timely follow-up rather than discovering them later." },
      { q: "Can you inspect a furnished or occupied apartment?", a: "We can, though access to areas behind furniture may be limited and will be noted in the report." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
      { label: "Resale property inspection", href: "/resale-property-inspection-dubai" },
    ],
  },

  "resale-property-inspection-dubai": {
    breadcrumb: "Resale Property Inspection Dubai",
    title: "Resale Property Inspection Dubai | Pre-Purchase | Fedari",
    description: "Buying an existing property in Dubai? Get an independent visual inspection of accessible areas with photographic findings. Request a fixed quote from Fedari.",
    h1: "Resale Property Inspections Before Purchase in Dubai",
    serviceType: "Resale / pre-purchase inspection",
    intro: "Before buying an existing apartment, villa or townhouse, obtain an independent record of its observable condition. Fedari documents relevant visible wear, signs of previous repairs, moisture indications and accessible finishing or service concerns within the agreed inspection scope.",
    whatWeInspect: [
      "General condition and signs of wear or ageing",
      "Evidence of previous repairs or ongoing damp",
      "Walls, ceilings, floors and visible cracking",
      "Doors, windows and seals",
      "Kitchen and bathroom condition and sealant",
      "Functional water flow and pressure observations where safe access and operating conditions allow",
      "Electrical fittings and visible wiring condition",
      "Air-conditioning age, performance and airflow",
    ],
    whoFor: [
      "Buyers of resale apartments, villas and townhouses",
      "Investors assessing a property before purchase",
      "Anyone who wants an independent condition report to negotiate price",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Penthouses"],
    process: [
      "Book a pre-purchase inspection before you exchange or transfer.",
      "We assess the real, current condition of the property.",
      "Each issue is logged with a photo and recommended next action.",
      "You receive the report after the inspection is completed to inform your decision. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing.",
    ],
    equipment: [
      "Moisture meters to detect damp and past leaks",
      "Thermal imaging, where included in the agreed scope, may reveal observable temperature anomalies; it does not see through finishes or confirm concealed defects",
      "Levels and measuring tools",
      "High-resolution cameras",
    ],
    deliverables: [
      "A pre-purchase condition report grouped by area and system",
      "Photographic evidence of each issue",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Resale inspections are visual and non-destructive and reflect condition on the inspection date only. They are not a structural engineering certification or a property valuation.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Tell us about any uncommissioned systems or access restrictions before booking so we can confirm what can be checked.",
    faqs: [
      { q: "Why inspect a resale property?", a: "An independent visual inspection provides a documented record of relevant observable concerns before purchase. This can support questions to the seller or further investigation; it does not eliminate all purchase risk." },
      { q: "Will the inspection find hidden problems?", a: "The inspection is limited to safely accessible, visible conditions within the agreed scope. Concealed defects may remain undetected. Where visible signs suggest a concern outside that scope, further specialist investigation may be recommended." },
      { q: "Is this a valuation or structural survey?", a: "No. It is a visual, non-destructive property-condition inspection, not a valuation, structural engineering survey or guarantee that the property is defect-free." },
      { q: "Can the report help me negotiate?", a: "Yes. A documented, independent condition report gives you factual grounds to renegotiate price or ask the seller to address issues before completion." },
    ],
    related: [
      { label: "Villa inspection Dubai", href: "/villa-inspection-dubai" },
      { label: "Apartment inspection Dubai", href: "/apartment-inspection-dubai" },
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
    ],
  },

  "dlp-warranty-inspection-dubai": {
    breadcrumb: "DLP / Warranty Inspection Dubai",
    title: "DLP Warranty Inspection Dubai | Fedari",
    description:
      "Independent DLP and warranty inspection in Dubai before your defect liability period expires. Report delivery within the confirmed timeframe.",
    h1: "DLP & warranty inspection in Dubai",
    serviceType: "DLP / warranty inspection",
    intro:
      "Fedari documents visible and accessible findings for submission to the developer, seller, contractor or other responsible party. Rectification and warranty coverage remain subject to the applicable contract, warranty terms and handover process. A defect-liability or warranty period is governed by the applicable contract and project documentation — its duration, coverage and rectification process can vary, so clients should confirm the relevant terms and deadline in their own documents. A Fedari DLP inspection documents visible defects before your applicable deadline, giving you clear, photographic evidence for timely follow-up.",
    whatWeInspect: [
      "Visible findings to document for your warranty submission",
      "Cracking, settlement indications and finishing failures",
      "Water ingress, damp and sealant breakdown",
      "Doors, windows and joinery that have moved or failed",
      "Plumbing leaks and drainage problems",
      "Electrical faults and fittings",
      "Air-conditioning performance and recurring issues",
    ],
    whoFor: [
      "Owners approaching the end of their defect liability period",
      "Buyers who took handover months ago and want a warranty check",
      "Owners who want visible defects documented before their applicable period ends",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Penthouses"],
    process: [
      "Book before your DLP / warranty deadline — timing matters.",
      "We document visible and accessible findings for your submission.",
      "Each defect is documented with a photo and recommended next action.",
      "You receive the report after the inspection is completed, ready to submit to the developer. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing.",
    ],
    equipment: [
      "Moisture meters for damp and leak detection",
      "Thermal imaging, where included in the agreed scope, may reveal observable temperature anomalies; it does not see through finishes or confirm concealed defects",
      "Levels and measuring tools",
      "High-resolution cameras",
    ],
    deliverables: [
      "A warranty-focused report grouped by area and system",
      "Photographic evidence to support your warranty claim",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "DLP inspections are visual and non-destructive. Whether a specific defect is covered by warranty is ultimately determined by your contract with the developer; we document the defect and its likely cause.",
    faqs: [
      { q: "What is a defect liability period?", a: "A defect-liability or warranty period is governed by the applicable contract and project documentation. Its duration, coverage and rectification process can vary. Clients should confirm the relevant terms and deadline in their own documents." },
      { q: "When should I book a DLP inspection?", a: "Comfortably before your warranty expires, so there is time to submit the report and have the developer complete repairs before the deadline." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
      { label: "Villa inspection Dubai", href: "/villa-inspection-dubai" },
    ],
  },

  "commercial-property-inspection-dubai": {
    breadcrumb: "Commercial Property Inspection Dubai",
    title: "Commercial Property Inspection Dubai | Fedari",
    description: "Visual condition inspections for agreed commercial properties in Dubai. Document accessible fit-out and finishing concerns. Request a scope review from Fedari.",
    h1: "Visual Commercial Property Inspections in Dubai",
    serviceType: "Commercial property inspection",
    image: {
      base: "/images/fedari-plaque-commercial",
      widths: [800, 1536],
      w: 1536,
      h: 1024,
      alt: "Fedari 'Inspected by Fedari' plaque at an inspected commercial premises",
    },
    priceMode: "commercial",
    intro: "Fedari provides independent visual condition inspections for agreed offices, retail units and other commercial spaces in Dubai. We document relevant observable finishing, fit-out and accessible service concerns within a clearly confirmed scope for practical follow-up.",
    whatWeInspect: [
      "Shell and core condition and finishing",
      "Fit-out quality, partitions, ceilings and flooring",
      "Doors, shopfronts, glazing and shutters",
      "Visible mechanical, electrical and plumbing services",
      "HVAC performance and distribution",
      "Fire and life-safety fittings that are visually accessible",
      "Accessible external and common areas",
    ],
    whoFor: [
      "Businesses taking handover of a new commercial unit",
      "Tenants completing a fit-out before opening",
      "Landlords documenting condition before or after a lease",
    ],
    propertyTypes: ["Offices", "Retail units", "Warehouses", "Showrooms"],
    process: [
      "Request a quote with your unit size and use — we scope the visit.",
      "We inspect the shell, fit-out and visible services.",
      "Each defect is documented with a photo and recommended next action.",
      "You receive the report after the inspection is completed. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
    ],
    equipment: [
      "Moisture meters for wet areas and roofs",
      "Thermal imaging, where included in the agreed scope, may reveal observable temperature anomalies; it does not see through finishes or confirm concealed defects",
      "Levels and measuring tools",
      "High-resolution cameras",
    ],
    deliverables: [
      "A commercial inspection report by area and system",
      "Photographic evidence of relevant visible findings where practicable",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Commercial inspections are visual and non-destructive. Statutory fire, MEP commissioning and structural certification remain the responsibility of the relevant licensed specialists.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Tell us about any uncommissioned systems or access restrictions before booking so we can confirm what can be checked.",
    faqs: [
      { q: "Can you inspect an occupied office or retail unit?", a: "Potentially, subject to the accepted scope, site permissions and safe access. Tell us about trading hours, occupied areas and restrictions before booking." },
      { q: "Does the report approve a fit-out or certify compliance?", a: "No. The report documents relevant visible conditions within the agreed scope. It is not regulatory approval, statutory certification or specialist system testing." },
      { q: "Can you inspect several units?", a: "Share the unit schedule, sizes, locations and access conditions. We review the proposed scope and availability before confirming the quotation or programme." },
      { q: "Do you inspect commercial fit-outs?", a: "Yes — we inspect both the developer shell-and-core and the tenant fit-out, documenting defects before you take occupancy or open for trading." },
      { q: "How is commercial pricing worked out?", a: "Commercial fees depend on the unit size and complexity. Send us the details and we will provide a fixed quote before you book." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
      { label: "Contact Fedari", href: "/contact" },
    ],
  },

  "property-inspection-abu-dhabi": {
    breadcrumb: "Property Inspection Abu Dhabi",
    title: "Property Inspection Abu Dhabi | Fedari",
    description:
      "Independent property snagging and handover inspection in Abu Dhabi. Villas, apartments and resale units documented with a photo report delivered within the confirmed timeframe.",
    h1: "Property inspection and snagging in Abu Dhabi",
    serviceType: "Property inspection",
    areaServed: ["Abu Dhabi"],
    intro:
      "Fedari Inspection provides independent property inspections across Abu Dhabi, from island developments to mainland and suburban communities. Whether you are taking handover of a new apartment on Reem or Yas Island, a villa on Saadiyat or in Khalifa City, or buying a resale unit, we document every visible finding with photographic evidence for your follow-up or purchase decision. Inspections in Abu Dhabi are subject to confirmed availability — share your property location when you enquire and we will confirm scheduling.",
    whatWeInspect: [
      "Finishing: paint, plaster, tiling and joinery",
      "Doors, windows, glazing and seals",
      "Kitchen and bathroom fit-out and sealant",
      "Functional water flow and pressure observations where safe access and operating conditions allow",
      "Electrical fittings and distribution",
      "Air-conditioning: operational, temperature and airflow observations within the agreed inspection scope",
      "Accessible external areas and, for villas, roof and grounds",
    ],
    whoFor: [
      "Buyers taking handover in Abu Dhabi developments",
      "Owners nearing the end of a defect liability period",
      "Resale buyers who want an independent condition report",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Penthouses"],
    process: [
      "Request an inspection at your Abu Dhabi property.",
      "Our inspector documents relevant visible findings within the agreed scope by area and system.",
      "You receive a structured photo report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
      "You use the report with your developer or in your purchase.",
    ],
    equipment: [
      "Moisture meters and, where included, thermal imaging",
      "Levels and measuring tools",
      "High-resolution cameras",
    ],
    deliverables: [
      "A structured inspection report by area and system",
      "Photographic evidence of relevant visible findings where practicable",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Inspections in Abu Dhabi are visual and non-destructive and reflect condition on the inspection date only. Service in this emirate is subject to confirmed availability, and the exact scope, access requirements and fixed fee are confirmed before booking. Local availability does not imply municipality, RERA, DLD or government appointment or approval.",
    faqs: [
      { q: "Do you cover Abu Dhabi as well as Dubai?", a: "Yes. Fedari Inspection carries out inspections across Abu Dhabi in addition to Dubai and the wider UAE." },
      { q: "Which Abu Dhabi areas do you serve?", a: "We serve Abu Dhabi's island and mainland communities. Share your property location when you enquire and we will confirm availability." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Property inspection Sharjah", href: "/property-inspection-sharjah" },
      { label: "Villa inspection Dubai", href: "/villa-inspection-dubai" },
    ],
  },

  "property-inspection-sharjah": {
    breadcrumb: "Property Inspection Sharjah",
    title: "Property Inspection Sharjah | Fedari",
    description:
      "Independent property snagging and handover inspection in Sharjah. Apartments, villas and resale homes documented with a photo report delivered within the confirmed timeframe.",
    h1: "Property inspection and snagging in Sharjah",
    serviceType: "Property inspection",
    areaServed: ["Sharjah"],
    intro:
      "Fedari Inspection carries out independent property inspections across Sharjah's growing residential communities. As new master developments hand over apartments and villas, an independent snagging inspection ensures build and finishing findings are documented rather than left for you to discover after you move in. Inspections in Sharjah are subject to confirmed availability — share your property location when you enquire and we will confirm scheduling.",
    whatWeInspect: [
      "Finishing: paint, plaster, tiling and joinery",
      "Doors, windows, glazing and seals",
      "Kitchen and bathroom fit-out and sealant",
      "Functional water flow and pressure observations where safe access and operating conditions allow",
      "Electrical fittings and distribution",
      "Air-conditioning: operational, temperature and airflow observations within the agreed inspection scope",
      "Accessible external areas and, for villas, roof and grounds",
    ],
    whoFor: [
      "Buyers taking handover in Sharjah developments",
      "Owners nearing the end of a defect liability period",
      "Resale buyers who want an independent condition report",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses"],
    process: [
      "Request an inspection at your Sharjah property.",
      "Our inspector documents relevant visible findings within the agreed scope by area and system.",
      "You receive a structured photo report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
      "You use the report with your developer or in your purchase.",
    ],
    equipment: [
      "Moisture meters and, where included, thermal imaging",
      "Levels and measuring tools",
      "High-resolution cameras",
    ],
    deliverables: [
      "A structured inspection report by area and system",
      "Photographic evidence of relevant visible findings where practicable",
      "Severity rating and recommended next action per item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Inspections in Sharjah are visual and non-destructive and reflect condition on the inspection date only. Service in this emirate is subject to confirmed availability, and the exact scope, access requirements and fixed fee are confirmed before booking. Local availability does not imply municipality, RERA, DLD or government appointment or approval.",
    faqs: [
      { q: "Do you carry out inspections in Sharjah?", a: "Yes. Fedari Inspection serves Sharjah alongside Dubai, Abu Dhabi and the wider UAE." },
      { q: "Can you inspect new master-development handovers?", a: "Yes — new community handovers are exactly where an independent snagging inspection adds the most value. Share your community and unit details when you enquire." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Property inspection Abu Dhabi", href: "/property-inspection-abu-dhabi" },
      { label: "Apartment inspection Dubai", href: "/apartment-inspection-dubai" },
    ],
  },

  "services/overseas-property-inspection-dubai": {
    breadcrumb: "Overseas Property Inspection",
    title: "Overseas Property Inspection Dubai | Fedari Inspection",
    description:
      "Own or buying Dubai property from abroad? Fedari inspects on your behalf — independent, photo-led report delivered within the confirmed timeframe. Fixed quote before booking.",
    h1: "Overseas property inspection in Dubai",
    serviceType: "Property inspection for overseas owners",
    intro:
      "Owning or buying a Dubai property from another country should not mean accepting it unseen. Fedari Inspection carries out the inspection on your behalf — once lawful and safe access is arranged — and delivers a clear, human-reviewed photographic report digitally, so you can manage handover, purchase or warranty follow-up from wherever you are.",
    whatWeInspect: [
      "Walls, ceilings, floors, paint finish and visible cracks",
      "Floor and wall tiling, grout, skirting and level",
      "Doors, windows, glazing, seals, frames and locks",
      "Kitchen and bathroom joinery, cabinets and worktops",
      "Sealant and silicone lines around wet areas",
      "Functional water flow and pressure observations where safe access and operating conditions allow",
      "Air-conditioning: operational, temperature and airflow observations within the agreed inspection scope",
      "Accessible electrical fittings, sockets and switches",
      "Balconies, terraces and external areas where lawful, safe and included in the agreed scope",
    ],
    whoFor: [
      "Overseas owners taking handover of a new Dubai property",
      "International buyers purchasing remotely",
      "Investors and landlords managing UAE property from abroad",
      "Owners approaching the end of a defect-liability period while overseas",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Penthouses"],
    process: [
      "Share the property details and your preferred date — we confirm scope, access requirements and a fixed fee.",
      "You or your appointed representative arranges lawful, safe access with the developer, seller or building management.",
      "Our inspector attends and assesses the agreed visible and accessible areas.",
      "You receive the photographic report digitally. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
      "The findings can be followed up with the responsible party, subject to the applicable contract and process — we remain reachable on WhatsApp throughout.",
    ],
    equipment: [
      "Moisture meters for damp and leak indications",
      "Thermal imaging, where included in the agreed scope, may reveal observable temperature anomalies; it does not see through finishes or confirm concealed defects",
      "Spirit and laser levels for tiling and joinery",
      "High-resolution cameras for photographic evidence",
    ],
    deliverables: [
      "A structured report grouped by room and system, delivered digitally",
      "Relevant visible findings photographed and rated Critical / Major / Minor / Advisory",
      "A clear recommended next action for each item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "Overseas inspections are visual and non-destructive, within the agreed scope. The client (or their appointed representative) arranges appointments, permissions and any developer approval needed to enter the property. Areas that are unsafe or inaccessible on the day are noted as not inspected in the report.",
    faqs: [
      { q: "Do I need to be in Dubai for the inspection?", a: "No. Most overseas clients are not present. Once lawful and safe access is arranged — by you or your appointed representative — we inspect on your behalf and deliver the report digitally." },
      { q: "How is access arranged if I am abroad?", a: "You or your representative (for example an agent, family member or property manager) coordinates access with the developer, seller or building management. We then schedule the visit with them directly." },
      { q: "How do I receive the report overseas?", a: "Digitally — you receive the full photographic report wherever you are in the world. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing." },
      { q: "Can you handle a handover inspection while I am abroad?", a: "Yes — this is one of the most common overseas requests. Findings are documented before acceptance, so they can be raised within the developer's handover process while you remain abroad." },
      { q: "Can you inspect near the end of my defect-liability period?", a: "Yes. A DLP inspection documents visible and accessible findings while they can still be submitted under the applicable warranty terms, subject to your contract." },
    ],
    related: [
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
      { label: "DLP / warranty inspection", href: "/dlp-warranty-inspection-dubai" },
      { label: "Resale property inspection", href: "/resale-property-inspection-dubai" },
    ],
  },

  "property-inspection-uae": {
    breadcrumb: "Property Inspection UAE",
    title: "Property Inspection UAE | Independent Snagging & Handover | Fedari",
    description:
      "Independent property inspections in Dubai and selected UAE locations, subject to confirmed availability: snagging, handover, apartment, villa, resale, DLP, commercial and overseas inspections. Report delivery within the confirmed timeframe.",
    h1: "Independent property inspections in Dubai and selected UAE locations",
    serviceType: "Property inspection",
    intro:
      "Fedari Inspection provides independent, visual and non-destructive property inspections in Dubai and selected UAE locations — property snagging, pre-handover, apartment, villa, resale, DLP/warranty, commercial, reinspection and overseas-owner inspections. Every inspection follows a confirmed scope and delivers a clear, human-reviewed photographic report with delivery within the confirmed timeframe. Dubai is our primary market, with Abu Dhabi and Sharjah served subject to confirmed availability.",
    whatWeInspect: [
      "Walls, ceilings, floors, paint finish and visible cracks",
      "Floor and wall tiling, grout, skirting and level",
      "Doors, windows, glazing, seals, frames and locks",
      "Kitchen and bathroom joinery, cabinets and worktops",
      "Sealant and silicone lines around wet areas",
      "Functional water flow and pressure observations where safe access and operating conditions allow",
      "Air-conditioning: operational, temperature and airflow observations within the agreed inspection scope",
      "Accessible electrical fittings, sockets and switches",
      "Balconies, terraces and external areas where lawful, safe and included in the agreed scope",
    ],
    whoFor: [
      "Buyers approaching handover in Dubai, Abu Dhabi or Sharjah",
      "Owners inside a defect-liability or warranty period",
      "Resale buyers who want condition evidence before committing",
      "Investors, landlords and overseas owners",
      "Commercial tenants and portfolio owners",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Penthouses", "Commercial units"],
    process: [
      "Share the emirate, community, property type, size and preferred date.",
      "We confirm availability, scope, access requirements and a fixed fee before booking.",
      "A Fedari inspector assesses the agreed safe, accessible and observable areas.",
      "You receive the photographic report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme.",
      "Findings can be followed up with the responsible party, subject to the applicable contract and process.",
    ],
    equipment: [
      "Moisture meters for damp and leak indications",
      "Thermal imaging, where included in the agreed scope, may reveal observable temperature anomalies; it does not see through finishes or confirm concealed defects",
      "Spirit and laser levels for tiling and joinery",
      "High-resolution cameras for photographic evidence",
    ],
    deliverables: [
      "A structured report grouped by area and system",
      "Relevant visible findings photographed and rated Critical / Major / Minor / Advisory",
      "A clear recommended next action for each item",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "All inspections are visual and non-destructive, within the agreed scope, and reflect the condition observed on the inspection date. Service outside Dubai (including Abu Dhabi and Sharjah) is subject to confirmed availability. The exact scope, access requirements and fixed fee are confirmed before booking. Local availability does not imply municipality, RERA, DLD or government appointment or approval.",
    faqs: [
      { q: "Which emirates does Fedari serve?", a: "Dubai is our primary market; Abu Dhabi and Sharjah are served subject to confirmed availability. Share your community and preferred date and we confirm availability with your exact quote." },
      { q: "Which inspection do I need?", a: "About to accept a new property: pre-handover snagging. Inside your warranty window: a DLP inspection. Buying resale: a resale inspection. Verifying repairs: a reinspection. The service pages below explain each one." },
      { q: "How much does an inspection cost in the UAE?", a: "Snagging starts from AED 799 for a studio apartment; villas from AED 2,499. The exact fixed fee depends on property type, size, location, access and scope — always confirmed before booking." },
      { q: "How fast is the report?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
      { q: "Is Fedari independent?", a: "Yes. Fedari Building Inspection L.L.C (UAE Trade Licence 1647070) works only for the client and is not affiliated with any developer, contractor or agent." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Property inspection Abu Dhabi", href: "/property-inspection-abu-dhabi" },
      { label: "Property inspection Sharjah", href: "/property-inspection-sharjah" },
      { label: "Dubai communities", href: "/property-snagging-dubai-communities" },
      { label: "Overseas property inspection", href: "/services/overseas-property-inspection-dubai" },
    ],
  },

  "reinspection-dubai": {
    breadcrumb: "Reinspection Dubai",
    title: "Property Reinspection & De-Snagging Dubai | Fedari",
    description: "Check the visible status of previously reported snagging items in Dubai. Updated photographic findings within an agreed reinspection scope. Request a Fedari quote.",
    h1: "Property Reinspection and De-Snagging in Dubai",
    serviceType: "Property reinspection",
    image: {
      base: "/images/fedari-certificate",
      widths: [700, 1051],
      w: 1055,
      h: 1491,
      portrait: true,
      alt: "Fedari Inspection Clearance Certificate marked cleared after reinspection",
    },
    intro: "When reported snagging items have been addressed, a reinspection documents their apparent visible status. Fedari revisits agreed accessible areas and compares observations with the original Fedari report, so follow-up is based on recorded evidence rather than an assumed completion.",
    whatWeInspect: [
      "The apparent, visible status of each previously reported finding",
      "Whether reported rectification is observable in the accessible areas",
      "New visible issues in the revisited areas, where reasonably apparent",
      "Photographic updates for comparison with the original report",
    ],
    whoFor: [
      "Owners whose developer reported snagging items as fixed",
      "Buyers verifying repairs before completing a resale purchase",
      "Landlords confirming works between tenancies",
      "Owners closing out a defect-liability period",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses"],
    process: [
      "Share the original Fedari report and confirm access to the property.",
      "We confirm the reinspection scope (the previously reported findings) and a fixed fee — from AED 399 — before booking.",
      "Our inspector revisits and visually reviews the apparent status of each reported item.",
      "You receive an updated photographic report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing.",
      "Items still open can be followed up again with the responsible party, subject to the applicable contract and process.",
    ],
    equipment: [
      "High-resolution cameras for comparative photographic evidence",
      "Moisture meters for damp and leak indications",
      "Spirit and laser levels for alignment checks",
    ],
    deliverables: [
      "Item-by-item status review of the original findings",
      "Updated photographs where the status changed",
      "A clear list of resolved vs still-open items",
      "Report delivery within the confirmed timeframe",
    ],
    scopeLimits:
      "A reinspection is a visual, non-destructive review of the apparent status of previously reported findings, within the agreed scope. It does not certify concealed work, guarantee that rectification is technically complete, or confirm acceptance by any responsible party.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Tell us about any uncommissioned systems or access restrictions before booking so we can confirm what can be checked.",
    faqs: [
      { q: "When should I book a reinspection?", a: "Request a return visit after the responsible party reports the relevant items as addressed and access can be arranged. Share any practical deadline so availability can be confirmed." },
      { q: "Is a reinspection a new full inspection?", a: "No. It reviews the agreed earlier findings. A wider or new inspection needs a separately confirmed scope." },
      { q: "Does a resolved finding certify the property?", a: "No. The status reflects the observable condition of the reviewed item at that visit. It is not statutory certification or a guarantee about concealed work or the entire property." },
      { q: "How much does a reinspection cost in Dubai?", a: "Reinspections start from AED 399, depending on the number of items and property access. The exact fixed fee is confirmed with your quote before booking." },
      { q: "When should I book a reinspection?", a: "After the developer, seller or contractor reports the items from your original report as rectified — and ideally before any final sign-off, payment milestone or warranty expiry." },
      { q: "Does a reinspection guarantee the repairs are correct?", a: "No. A reinspection is a visual review of the apparent status of previously reported findings within the agreed scope — it cannot certify concealed work or guarantee technical completeness." },
      { q: "Do I need the original report?", a: "Yes — the reinspection scope is built from the findings in your original Fedari report, so each item can be reviewed against its documented state." },
      { q: "How fast is the reinspection report?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "DLP / warranty inspection", href: "/dlp-warranty-inspection-dubai" },
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
    ],
  },

  "renovation-snagging-dubai": {
    breadcrumb: "Renovation Snagging Dubai",
    title: "Renovation Property Snagging Dubai | Fedari",
    description: "Independent snagging after renovation or fit-out works in Dubai. Visible workmanship reviewed, defects documented with photos. Request a Fedari quote.",
    h1: "Renovation Property Snagging in Dubai",
    serviceType: "Renovation snagging inspection",
    image: {
      base: "/images/fedari-inspector-wall",
      widths: [800, 1280, 1728],
      w: 1728,
      h: 1152,
      alt: "Illustrative inspection of interior wall finishes after renovation works",
    },
    intro: "Renovation and fit-out works do not always finish to the agreed standard. Fedari visually reviews the accessible, completed works against the agreed scope and finish expectations, so visible defects and unfinished items are documented before final payments or sign-off.",
    whatWeInspect: [
      "Visible workmanship of renovation and fit-out finishes",
      "Walls, ceilings, flooring, tiling and paintwork completed in the works",
      "Doors, joinery and fittings installed or altered during the renovation",
      "Visible plumbing and electrical points affected by the works",
      "Visible sealant, grouting and junction details in renewed wet areas",
    ],
    whoFor: [
      "Owners reviewing a contractor's completed renovation",
      "Buyers assessing a recently renovated property before purchase",
      "Landlords checking works between tenancies",
      "Investors verifying fit-out quality before handover of payments",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Commercial units"],
    process: [
      "Share the renovation scope or contractor agreement summary and confirm access.",
      "We confirm the inspection scope and your exact fixed fee before booking.",
      "Our inspector visually reviews the accessible completed works and photographs relevant findings.",
      "You receive a documented report with findings prioritised Low, Medium or High, delivered within your confirmed timeframe.",
    ],
    equipment: [
      "High-resolution cameras for photographic evidence",
      "Moisture meters for damp indications in renewed wet areas",
      "Spirit and laser levels for alignment checks",
    ],
    deliverables: [
      "Photographic findings organised by area",
      "Low / Medium / High priority ratings",
      "Clear observations to discuss with your contractor",
      "Report delivery within your confirmed timeframe",
    ],
    scopeLimits:
      "A renovation snagging inspection is visual and non-destructive and covers the agreed safe, accessible and observable areas. It does not verify concealed works, structural elements, or compliance of the renovation with permits, and it does not guarantee that every defect has been identified.",
    access: "Arrange lawful access and operating electricity, water and air conditioning so completed works can be observed in use where relevant. Tell us about any areas still under construction before booking so we can confirm what can be checked.",
    faqs: [
      { q: "When should I book a renovation snagging inspection?", a: "Ideally after the contractor reports completion but before final payment or sign-off, while rectification is still practical to request." },
      { q: "Do you check the renovation against the contract?", a: "We review the visible, accessible works against the agreed scope information you share with us. We do not verify contractual compliance, permits or concealed work." },
      { q: "Can you inspect while works are still ongoing?", a: "Yes, a site inspection during works can be arranged — tell us the project stage so we can confirm a suitable scope." },
      { q: "What does the report include?", a: "Photographic findings organised by location, each rated Low, Medium or High, so you can raise specific points with the contractor. Delivery is within your confirmed timeframe." },
    ],
    related: [
      { label: "Site inspection Dubai", href: "/site-inspection-dubai" },
      { label: "Resale property inspection", href: "/resale-property-inspection-dubai" },
      { label: "Defect cost estimation", href: "/defect-cost-estimation-dubai" },
    ],
  },

  "spa-inspection-dubai": {
    breadcrumb: "SPA Inspection Dubai",
    title: "SPA Based Inspection Dubai | Sale & Purchase Agreement | Fedari",
    description: "Independent inspection against your Sale and Purchase Agreement in Dubai. Visible finishes and inclusions reviewed before completion. Request a Fedari quote.",
    h1: "SPA Based Inspection in Dubai",
    serviceType: "SPA based inspection",
    image: {
      base: "/images/fedari-plaque-villa",
      widths: [800, 1536],
      w: 1536,
      h: 1024,
      alt: "Fedari 'Inspected by Fedari' plaque at a villa entrance in Dubai",
    },
    intro: "A Sale and Purchase Agreement (SPA) sets out what the developer or seller promised to deliver. Fedari visually compares the accessible, observable property against the finishes, specifications and inclusions documented in your SPA, so deviations are recorded before you complete.",
    whatWeInspect: [
      "Visible finishes against the SPA specifications you share with us",
      "Documented inclusions: appliances, fittings and fixtures where observable",
      "Visible workmanship against the agreed finish level",
      "Snagging items within the agreed inspection scope",
      "Photographic evidence of relevant visible deviations",
    ],
    whoFor: [
      "Off-plan buyers approaching completion under an SPA",
      "Resale buyers checking a property against the agreed inclusions",
      "Overseas owners who cannot attend the inspection themselves",
      "Investors documenting deviations before final payment",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses"],
    process: [
      "Share the relevant SPA pages (specifications and inclusions) and confirm access.",
      "We confirm the inspection scope and your exact fixed fee before booking.",
      "Our inspector visually reviews the accessible property against the shared documents.",
      "You receive a photographic report with findings prioritised Low, Medium or High, delivered within your confirmed timeframe.",
    ],
    equipment: [
      "High-resolution cameras for photographic evidence",
      "Moisture meters for damp and leak indications",
      "Spirit and laser levels for alignment checks",
    ],
    deliverables: [
      "Photographic findings organised by location",
      "Observations referenced to the shared SPA information where relevant",
      "Low / Medium / High priority ratings",
      "Report delivery within your confirmed timeframe",
    ],
    scopeLimits:
      "An SPA based inspection is visual and non-destructive and covers the agreed safe, accessible and observable areas against the documents you provide. It is not a legal review of the SPA, does not interpret contractual rights, and does not guarantee the identification of concealed or future defects.",
    access: "Arrange lawful access, any required building or developer permission, and operating electricity, water and air conditioning. In Dubai, confirm that the required DEWA connections are active. Share the relevant SPA specification pages before the inspection date.",
    faqs: [
      { q: "What do you need from my SPA?", a: "The pages describing finishes, specifications and inclusions for your unit are enough. We confirm the exact scope with you before booking." },
      { q: "Is this a legal review of my contract?", a: "No. We document the visible, observable condition of the property against the shared information. For legal interpretation of your SPA, consult your legal advisor." },
      { q: "When in the purchase process should I book?", a: "Most buyers book shortly before completion or handover, when the unit is finished and access can be arranged." },
      { q: "What does the report include?", a: "Photographic findings organised by location and rated Low, Medium or High, so you can raise specific observations with the developer or seller. Delivery is within your confirmed timeframe." },
    ],
    related: [
      { label: "Property snagging Dubai", href: "/property-snagging-dubai" },
      { label: "Handover inspection Dubai", href: "/handover-inspection-dubai" },
      { label: "Resale property inspection", href: "/resale-property-inspection-dubai" },
    ],
  },

  "mep-inspection-dubai": {
    breadcrumb: "MEP Inspection Dubai",
    title: "MEP Inspection Dubai | Mechanical, Electrical, Plumbing | Fedari",
    description: "Independent MEP inspection in Dubai: visible mechanical, electrical and plumbing systems reviewed and documented with photos. Request a Fedari quote.",
    h1: "MEP Inspections in Dubai",
    serviceType: "MEP inspection",
    image: {
      base: "/images/fedari-inspector-pipes",
      widths: [800, 1280, 1728],
      w: 1728,
      h: 1152,
      alt: "Illustrative inspection of accessible mechanical and plumbing installations",
    },
    intro: "Mechanical, electrical and plumbing (MEP) systems are expensive to fix after handover. Fedari visually reviews the accessible and observable parts of these systems and documents visible defects, poor workmanship and incomplete installations within the agreed scope.",
    whatWeInspect: [
      "Visible plumbing: pipework, fixtures, drainage points and signs of leaks",
      "Accessible electrical points: sockets, switches, visible wiring and the distribution board exterior",
      "AC / HVAC units, vents and visible ductwork where accessible",
      "Visible water heaters, pumps and mechanical equipment where accessible",
      "Visible workmanship of MEP penetrations, sealing and finishes",
    ],
    whoFor: [
      "Buyers at handover of new apartments, villas and townhouses",
      "Owners with recurring MEP concerns who want documented findings",
      "Landlords reviewing systems between tenancies",
      "Commercial tenants assessing a unit before fit-out",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Commercial units"],
    process: [
      "Tell us the property type and your MEP concerns, and confirm access.",
      "We confirm the inspection scope and your exact fixed fee before booking.",
      "Our inspector visually reviews the accessible systems and photographs relevant findings.",
      "You receive a documented report with findings prioritised Low, Medium or High, delivered within your confirmed timeframe.",
    ],
    equipment: [
      "High-resolution cameras for photographic evidence",
      "Moisture meters for damp and leak indications",
      "Basic electrical testers for accessible socket checks where in scope",
    ],
    deliverables: [
      "Photographic findings organised by system and location",
      "Low / Medium / High priority ratings",
      "Clear observations to raise with the responsible party",
      "Report delivery within your confirmed timeframe",
    ],
    scopeLimits:
      "An MEP inspection is visual and non-destructive and covers the agreed safe, accessible and observable areas. It does not include opening walls, pressure-testing concealed lines, electrical certification, or performance guarantees, and it does not replace testing by licensed MEP contractors where required.",
    access: "Arrange lawful access and operating electricity, water and air conditioning so systems can be observed in operation where relevant. In Dubai, confirm that the required DEWA connections are active. Tell us about uncommissioned systems before booking.",
    faqs: [
      { q: "Is an MEP inspection part of a standard snagging inspection?", a: "Visible MEP observations are part of a snagging inspection. A dedicated MEP inspection goes deeper into the accessible systems and is scoped separately." },
      { q: "Do you certify the electrical or plumbing installations?", a: "No. We document visible observations. Certification or compliance testing must be done by the appropriately licensed parties." },
      { q: "Can you check concealed pipework or wiring?", a: "No concealed works are inspected — the review is visual and non-destructive, limited to accessible and observable areas." },
      { q: "What does the report include?", a: "Photographic findings organised by system and location, rated Low, Medium or High. Delivery is within your confirmed timeframe." },
    ],
    related: [
      { label: "HVAC duct inspection", href: "/hvac-duct-inspection-dubai" },
      { label: "Electrical load and DB testing", href: "/electrical-load-db-testing-dubai" },
      { label: "Pressure and smoke testing", href: "/pressure-smoke-testing-dubai" },
    ],
  },

  "mold-inspection-dubai": {
    breadcrumb: "Mold Inspection Dubai",
    title: "Mold Inspection Dubai | Visual Mold & Moisture Review | Fedari",
    description: "Independent visual mold and moisture inspection in Dubai. Visible growth, staining and moisture indications documented with photos. Request a Fedari quote.",
    h1: "Mold Inspections in Dubai",
    serviceType: "Mold inspection",
    image: {
      base: "/images/fedari-inspector-wall",
      widths: [800, 1280, 1728],
      w: 1728,
      h: 1152,
      alt: "Illustrative inspection of an interior wall for visible mold and moisture signs",
    },
    intro: "Visible mold and moisture staining often point to condensation, leaks or ventilation problems. Fedari visually reviews accessible areas for visible mold, staining and moisture indications and documents the affected locations so you can arrange the right remedial work.",
    whatWeInspect: [
      "Visible mold growth on walls, ceilings, joinery and around windows",
      "Moisture staining, bubbling paint and other damp indications",
      "Moisture meter readings at accessible suspect locations",
      "Visible ventilation provisions in wet rooms and kitchens",
      "Visible signs around AC units, balconies and external walls where accessible",
    ],
    whoFor: [
      "Owners or tenants seeing visible mold or musty odours",
      "Buyers checking a property for moisture history before purchase",
      "Landlords documenting conditions between tenancies",
      "Parents and sensitive occupants wanting locations documented",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses"],
    process: [
      "Tell us where you have seen mold, staining or odours, and confirm access.",
      "We confirm the inspection scope and your exact fixed fee before booking.",
      "Our inspector visually reviews the accessible areas and takes moisture indications where relevant.",
      "You receive a photographic report with affected locations prioritised Low, Medium or High, delivered within your confirmed timeframe.",
    ],
    equipment: [
      "Moisture meters for damp indications",
      "High-resolution cameras for photographic evidence",
      "Torches and mirrors for visible checks in dark or awkward accessible areas",
    ],
    deliverables: [
      "Photographic documentation of visible mold and moisture indications",
      "Affected locations organised by area with Low / Medium / High ratings",
      "Practical observations to discuss with remediation or maintenance parties",
      "Report delivery within your confirmed timeframe",
    ],
    scopeLimits:
      "A mold inspection is visual and non-destructive and covers the agreed safe, accessible and observable areas. It is not a laboratory air-quality or surface sampling test, does not identify mold species or health risks, and does not confirm the concealed extent behind finishes.",
    access: "Arrange lawful access and operating electricity and air conditioning. Do not clean or treat visible mold before the inspection where possible, so the condition can be documented accurately.",
    faqs: [
      { q: "Do you test air quality or take samples?", a: "No. This is a visual inspection with moisture indications at accessible locations. Laboratory testing, if needed, is a separate specialist service." },
      { q: "Can you tell me what caused the mold?", a: "We document visible indications and likely visible contributing factors. The exact cause — especially concealed leaks — cannot be confirmed without invasive investigation." },
      { q: "Do you remove mold?", a: "No. Fedari documents findings only and does not carry out remediation or repairs." },
      { q: "What does the report include?", a: "Photographic findings organised by location and rated Low, Medium or High, so you can brief a remediation or maintenance party. Delivery is within your confirmed timeframe." },
    ],
    related: [
      { label: "Defect cost estimation", href: "/defect-cost-estimation-dubai" },
      { label: "Site inspection Dubai", href: "/site-inspection-dubai" },
      { label: "Resale property inspection", href: "/resale-property-inspection-dubai" },
    ],
  },

  "pressure-smoke-testing-dubai": {
    breadcrumb: "Pressure & Smoke Testing Dubai",
    title: "Pressure and Smoke Testing Dubai | Plumbing & Drainage | Fedari",
    description: "Pressure and smoke testing in Dubai for accessible plumbing and drainage lines. Defects documented with photos within an agreed scope. Request a Fedari quote.",
    h1: "Pressure and Smoke Testing in Dubai",
    serviceType: "Pressure and smoke testing",
    image: {
      base: "/images/fedari-inspector-pipes",
      widths: [800, 1280, 1728],
      w: 1728,
      h: 1152,
      alt: "Illustrative pressure gauge connected to accessible plumbing during testing",
    },
    intro: "Pressure testing and smoke testing help reveal leaks and faults in accessible plumbing and drainage that a purely visual look can miss. Fedari carries out these checks within an agreed, accessible scope and documents the observations with photographs.",
    whatWeInspect: [
      "Pressure test observations on accessible plumbing lines, where connections allow",
      "Smoke test observations on accessible drainage and waste runs, where in scope",
      "Visible signs of leakage during and after testing",
      "Accessible traps, joints and connections during the test",
      "Photographic documentation of any observed smoke escape or pressure drop",
    ],
    whoFor: [
      "Owners with unexplained odours, damp patches or suspected leaks",
      "Buyers who want accessible drainage checked before purchase",
      "Property managers investigating recurring plumbing complaints",
      "Owners after renovation works affecting plumbing or drainage",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Commercial units"],
    process: [
      "Describe the concern and property layout, and confirm access to plumbing points.",
      "We confirm the testable scope and your exact fixed fee before booking.",
      "Our inspector performs the agreed tests at accessible points and documents the observations.",
      "You receive a photographic report with findings prioritised Low, Medium or High, delivered within your confirmed timeframe.",
    ],
    equipment: [
      "Pressure testing equipment for accessible plumbing lines",
      "Smoke generation equipment for accessible drainage runs",
      "High-resolution cameras for photographic evidence",
    ],
    deliverables: [
      "Photographic documentation of test observations",
      "Findings organised by location with Low / Medium / High ratings",
      "Clear observations to brief a licensed plumber or the responsible party",
      "Report delivery within your confirmed timeframe",
    ],
    scopeLimits:
      "Testing is limited to accessible points within the agreed scope and normal operating conditions. It does not cover concealed pipework, guarantee the absence of leaks, or replace diagnostic work by licensed plumbing contractors. Results reflect the conditions at the time of testing.",
    access: "Arrange lawful access and operating water supply. In Dubai, confirm that the required DEWA connections are active. Access to bathrooms, kitchens and any accessible drainage points is needed; tell us about sealed or concealed runs before booking.",
    faqs: [
      { q: "Will the test find every leak?", a: "No test can guarantee that. The checks cover the accessible lines and points within the agreed scope and document what is observed at the time of testing." },
      { q: "Is smoke testing disruptive?", a: "Smoke testing uses visible, non-toxic smoke at accessible drainage points. Some temporary odour is possible; ventilate after the visit. We confirm the practical details when scoping." },
      { q: "Do you repair the leaks you find?", a: "No. Fedari documents findings only and does not carry out repairs." },
      { q: "What does the report include?", a: "Photographic findings organised by location and rated Low, Medium or High, so a licensed plumber or the responsible party can act on them. Delivery is within your confirmed timeframe." },
    ],
    related: [
      { label: "MEP inspection Dubai", href: "/mep-inspection-dubai" },
      { label: "Mold inspection Dubai", href: "/mold-inspection-dubai" },
      { label: "Defect cost estimation", href: "/defect-cost-estimation-dubai" },
    ],
  },

  "electrical-load-db-testing-dubai": {
    breadcrumb: "Electrical Load & DB Testing Dubai",
    title: "Electrical Load and DB Testing Dubai | Fedari",
    description: "Independent electrical load and distribution board review in Dubai. Accessible boards, breakers and load observations documented. Request a Fedari quote.",
    h1: "Electrical Load and DB Testing in Dubai",
    serviceType: "Electrical load and DB testing",
    image: {
      base: "/images/fedari-inspector-wall",
      widths: [800, 1280, 1728],
      w: 1728,
      h: 1152,
      alt: "Illustrative review of an accessible electrical distribution board",
    },
    intro: "The distribution board (DB) is the heart of a property's electrical installation. Fedari visually reviews accessible distribution boards, breakers and observable load conditions, and documents visible defects, signs of overheating and questionable workmanship within the agreed scope.",
    whatWeInspect: [
      "Accessible distribution boards: visible condition, labelling and arrangement",
      "Visible signs of overheating, scorching or corrosion at the DB",
      "Breakers and RCD / earth-leakage devices where observable",
      "Load observations on accessible circuits where safely measurable and in scope",
      "Visible workmanship of accessible electrical points and fittings",
    ],
    whoFor: [
      "Buyers assessing the electrical condition before purchase",
      "Owners with tripping breakers, flickering or burning smells",
      "Landlords documenting electrical condition between tenancies",
      "Commercial tenants reviewing a unit before fit-out",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Commercial units"],
    process: [
      "Tell us the property type and your electrical concerns, and confirm access to the DB.",
      "We confirm the inspection scope and your exact fixed fee before booking.",
      "Our inspector reviews the accessible board and points and documents observations.",
      "You receive a photographic report with findings prioritised Low, Medium or High, delivered within your confirmed timeframe.",
    ],
    equipment: [
      "Basic electrical test instruments for accessible measurements where in scope",
      "High-resolution cameras for photographic evidence",
      "Torches for visible checks inside accessible enclosures",
    ],
    deliverables: [
      "Photographic documentation of the accessible DB and observed points",
      "Findings organised by location with Low / Medium / High ratings",
      "Clear observations to brief a licensed electrician or the responsible party",
      "Report delivery within your confirmed timeframe",
    ],
    scopeLimits:
      "The review is limited to accessible, observable components within the agreed scope and is non-destructive. It is not an electrical installation certificate, does not verify concealed wiring, and does not replace testing or certification by a licensed electrical contractor.",
    access: "Arrange lawful access and operating electricity. In Dubai, confirm that the required DEWA connection is active. The distribution board and relevant rooms must be safely accessible; tell us about locked or shared electrical rooms before booking.",
    faqs: [
      { q: "Do you issue an electrical safety certificate?", a: "No. We document visible and measurable observations at accessible points. Certification belongs with licensed electrical contractors." },
      { q: "Can you test concealed wiring?", a: "No. The review is non-destructive and limited to accessible, observable components within the agreed scope." },
      { q: "Is load testing safe for my installation?", a: "Measurements are taken at accessible points within normal operating conditions. We confirm what can safely be tested when scoping your booking." },
      { q: "What does the report include?", a: "Photographic findings organised by location and rated Low, Medium or High, so a licensed electrician or the responsible party can act on them. Delivery is within your confirmed timeframe." },
    ],
    related: [
      { label: "MEP inspection Dubai", href: "/mep-inspection-dubai" },
      { label: "HVAC duct inspection", href: "/hvac-duct-inspection-dubai" },
      { label: "Site inspection Dubai", href: "/site-inspection-dubai" },
    ],
  },

  "hvac-duct-inspection-dubai": {
    breadcrumb: "HVAC Duct Inspection Dubai",
    title: "HVAC Duct Inspection Dubai | AC & Ductwork Review | Fedari",
    description: "Independent HVAC and duct inspection in Dubai. Accessible AC units, vents and visible ductwork reviewed and documented with photos. Request a Fedari quote.",
    h1: "HVAC Duct Inspections in Dubai",
    serviceType: "HVAC duct inspection",
    image: {
      base: "/images/fedari-inspector-pipes",
      widths: [800, 1280, 1728],
      w: 1728,
      h: 1152,
      alt: "Illustrative inspection of accessible AC ductwork and vents",
    },
    intro: "In Dubai's climate, air conditioning is not a luxury but a necessity. Fedari visually reviews accessible AC units, vents, grilles and visible ductwork, and documents visible contamination, damage, poor insulation and workmanship issues within the agreed scope.",
    whatWeInspect: [
      "Accessible AC indoor and outdoor units: visible condition and installation",
      "Vents, grilles and diffusers: visible contamination and condition",
      "Visible ductwork where accessible: damage, insulation and sealing",
      "Condensate drainage points and signs of leakage where observable",
      "Airflow observations at accessible outlets where relevant",
    ],
    whoFor: [
      "Buyers checking AC condition before handover or purchase",
      "Owners with weak cooling, odours or dust from vents",
      "Landlords documenting HVAC condition between tenancies",
      "Commercial tenants assessing a unit before occupation",
    ],
    propertyTypes: ["Apartments", "Villas", "Townhouses", "Commercial units"],
    process: [
      "Tell us the property type and your AC concerns, and confirm access to units and ceiling voids where applicable.",
      "We confirm the inspection scope and your exact fixed fee before booking.",
      "Our inspector visually reviews the accessible equipment and ductwork and photographs relevant findings.",
      "You receive a documented report with findings prioritised Low, Medium or High, delivered within your confirmed timeframe.",
    ],
    equipment: [
      "High-resolution cameras for photographic evidence",
      "Torches and mirrors for visible checks in accessible voids",
      "Thermometers for outlet air observations where relevant",
    ],
    deliverables: [
      "Photographic findings organised by unit and location",
      "Low / Medium / High priority ratings",
      "Clear observations to brief an AC maintenance party or the responsible party",
      "Report delivery within your confirmed timeframe",
    ],
    scopeLimits:
      "An HVAC duct inspection is visual and non-destructive and covers the agreed safe, accessible and observable areas. It does not include opening sealed ducts, air-quality or microbial testing, refrigerant work, or performance guarantees, and it does not replace servicing by licensed HVAC contractors.",
    access: "Arrange lawful access and operating electricity so the AC can be observed in operation where relevant. In Dubai, confirm that the required DEWA connection is active. Tell us about sealed ceiling voids or shared chiller systems before booking.",
    faqs: [
      { q: "Do you clean or service the ducts?", a: "No. Fedari documents findings only and does not carry out cleaning, servicing or repairs." },
      { q: "Can you inspect sealed or concealed ductwork?", a: "No. The review is limited to visible and accessible sections within the agreed scope." },
      { q: "Do you test air quality?", a: "No. We document visible contamination and airflow observations. Laboratory air-quality testing is a separate specialist service." },
      { q: "What does the report include?", a: "Photographic findings organised by unit and location, rated Low, Medium or High. Delivery is within your confirmed timeframe." },
    ],
    related: [
      { label: "MEP inspection Dubai", href: "/mep-inspection-dubai" },
      { label: "Mold inspection Dubai", href: "/mold-inspection-dubai" },
      { label: "Pressure and smoke testing", href: "/pressure-smoke-testing-dubai" },
    ],
  },
};

export const SERVICE_SLUGS = Object.keys(SERVICE_PAGES);
